import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api, { formatApiError } from '../../api';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';
import { Upload, CheckCircle2, Clock, ArrowLeft } from 'lucide-react';
import { BrandLogo } from '../BrandLogo';
import { PhotoPicker } from './PhotoPicker';
import { DocumentPicker } from './DocumentPicker';

const DriverRegistration = () => {
  const navigate = useNavigate();
  const { registerDriver } = useAuth();
  const [step, setStep] = useState('form'); // form, pending, approved
  const [submitting, setSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoUploaded, setPhotoUploaded] = useState(false);
  const [licenseFile, setLicenseFile] = useState(null);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [docsUploaded, setDocsUploaded] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    aadharNumber: '',
    drivingLicense: '',
    experience: '',
    address: '',
    gender: '',
  });
  const [generatedCredentials, setGeneratedCredentials] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.fullName.trim() || !formData.phone.trim() || !formData.email.trim()) {
      toast({ title: 'Missing Information', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    if (!formData.aadharNumber || formData.aadharNumber.length !== 12) {
      toast({ title: 'Invalid Aadhaar', description: 'Please enter a valid 12-digit Aadhaar number', variant: 'destructive' });
      return;
    }
    if (!formData.gender) {
      toast({ title: 'Select Gender', description: 'Please select your gender — riders can request female drivers', variant: 'destructive' });
      return;
    }
    if (!formData.drivingLicense.trim()) {
      toast({ title: 'Enter License', description: 'Please enter your driving license number', variant: 'destructive' });
      return;
    }
    if (!formData.experience) {
      toast({ title: 'Enter Experience', description: 'Please enter your driving experience', variant: 'destructive' });
      return;
    }
    if (!photoFile) {
      toast({ title: 'Add Your Photo', description: 'Please add a clear face photo — riders see it when you accept a booking', variant: 'destructive' });
      return;
    }
    if (!licenseFile) {
      toast({ title: 'Upload Driving License', description: 'Please upload a photo or PDF of your driving license', variant: 'destructive' });
      return;
    }
    if (!aadhaarFile) {
      toast({ title: 'Upload Aadhaar Card', description: 'Please upload a photo or PDF of your Aadhaar card', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      const data = await registerDriver({
        full_name: formData.fullName,
        phone: formData.phone,
        email: formData.email,
        aadhaar_number: formData.aadharNumber,
        driving_license: formData.drivingLicense,
        experience_years: parseInt(formData.experience, 10),
        address: formData.address,
        gender: formData.gender,
      });
      setGeneratedCredentials({ driverId: data.driver_id, password: data.password });
      try {
        const form = new FormData();
        form.append('driver_id', data.driver_id);
        form.append('password', data.password);
        form.append('file', photoFile);
        await api.post('/api/auth/driver/register-photo', form);
        setPhotoUploaded(true);
      } catch (photoErr) {
        toast({ title: 'Photo upload failed', description: `${formatApiError(photoErr)} — you can add it later from your dashboard.`, variant: 'destructive' });
      }
      try {
        const licenseForm = new FormData();
        licenseForm.append('driver_id', data.driver_id);
        licenseForm.append('password', data.password);
        licenseForm.append('doc_type', 'license');
        licenseForm.append('file', licenseFile);
        await api.post('/api/auth/driver/register-documents', licenseForm);

        const aadhaarForm = new FormData();
        aadhaarForm.append('driver_id', data.driver_id);
        aadhaarForm.append('password', data.password);
        aadhaarForm.append('doc_type', 'aadhaar');
        aadhaarForm.append('file', aadhaarFile);
        await api.post('/api/auth/driver/register-documents', aadhaarForm);
        setDocsUploaded(true);
      } catch (docErr) {
        toast({ title: 'Document upload failed', description: `${formatApiError(docErr)} — please contact support so we can verify your account.`, variant: 'destructive' });
      }
      setStep('pending');
      toast({
        title: 'Application Submitted!',
        description: 'Your details are under verification.',
        duration: 5000,
      });
    } catch (err) {
      toast({ title: 'Registration Failed', description: formatApiError(err), variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'approved') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Registration Complete!</h2>
              <p className="text-muted-foreground mb-6">
                Congratulations! Your driver account has been approved.
              </p>

              <div className="bg-accent/10 border-2 border-accent/30 rounded-lg p-6 mb-6">
                <p className="text-sm font-semibold text-primary mb-4">🔐 Your Login Credentials</p>
                <div className="space-y-3 text-left">
                  <div>
                    <p className="text-xs text-primary font-medium">Driver ID</p>
                    <p className="text-lg font-bold text-primary font-mono" data-testid="generated-driver-id">
                      {generatedCredentials?.driverId}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-primary font-medium">Password</p>
                    <p className="text-lg font-bold text-primary font-mono" data-testid="generated-driver-password">
                      {generatedCredentials?.password}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-primary mt-4">
                  ⚠️ Please save these credentials securely. You'll need them to login.
                </p>
              </div>

              <Button className="w-full bg-primary hover:bg-primary-dark" onClick={() => navigate('/driver/login')}>
                Go to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'pending') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-12 h-12 text-yellow-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Verification Pending</h2>
              <p className="text-muted-foreground mb-4">
                Your application is under review. We are verifying your documents.
              </p>
              {photoUploaded && (
                <p className="text-xs text-green-700 mb-4 flex items-center justify-center gap-1" data-testid="photo-uploaded-note">
                  <CheckCircle2 className="w-4 h-4" /> Profile photo uploaded
                </p>
              )}
              {docsUploaded && (
                <p className="text-xs text-green-700 mb-4 flex items-center justify-center gap-1" data-testid="docs-uploaded-note">
                  <CheckCircle2 className="w-4 h-4" /> Driving license &amp; Aadhaar card uploaded
                </p>
              )}

              {generatedCredentials && (
                <div className="bg-accent/10 border-2 border-accent/30 rounded-lg p-4 mb-4 text-left">
                  <p className="text-xs font-semibold text-primary mb-2">Save these credentials — you'll need them after approval:</p>
                  <p className="text-sm text-primary">
                    <span className="font-medium">Driver ID:</span>{' '}
                    <span className="font-mono font-bold" data-testid="pending-driver-id">{generatedCredentials.driverId}</span>
                  </p>
                  <p className="text-sm text-primary">
                    <span className="font-medium">Password:</span>{' '}
                    <span className="font-mono font-bold" data-testid="pending-driver-password">{generatedCredentials.password}</span>
                  </p>
                </div>
              )}

              <div className="bg-background border border-border rounded-lg p-4 mb-6">
                <p className="text-sm text-foreground">
                  This usually takes 24-48 hours. You'll be able to login once an admin approves your account.
                </p>
              </div>

              <Button variant="ghost" className="w-full mt-4" onClick={() => navigate('/')}>
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <BrandLogo variant="compact" className="h-8 w-auto" />
              <span className="text-sm font-semibold text-muted-foreground border-l pl-3">Become a Driver</span>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Driver Registration</CardTitle>
            <CardDescription>
              Join our network of professional drivers. Complete your profile and get verified to start earning.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-lg border border-border p-4 bg-background/60">
                <Label className="mb-3 block">Profile Photo *</Label>
                <PhotoPicker initials={formData.fullName?.[0]?.toUpperCase() || 'D'} onFile={setPhotoFile} testId="reg-photo" />
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Personal Details</h3>

                <div>
                  <Label htmlFor="fullName">Full Name *</Label>
                  <Input id="fullName" name="fullName" placeholder="Enter your full name"
                    value={formData.fullName} onChange={handleInputChange} className="h-12" required
                    data-testid="reg-fullname-input" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input id="phone" name="phone" type="tel" placeholder="10-digit number"
                      value={formData.phone} onChange={handleInputChange} maxLength={10} className="h-12" required
                      data-testid="reg-phone-input" />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" name="email" type="email" placeholder="your@email.com"
                      value={formData.email} onChange={handleInputChange} className="h-12" required
                      data-testid="reg-email-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="aadharNumber">Aadhaar Number *</Label>
                  <Input id="aadharNumber" name="aadharNumber" type="text" placeholder="Enter 12-digit Aadhaar number"
                    value={formData.aadharNumber} onChange={handleInputChange} maxLength={12} className="h-12" required
                    data-testid="reg-aadhaar-input" />
                  <p className="text-xs text-muted-foreground mt-1">Your Aadhaar will be verified for identity confirmation</p>
                </div>

                <div>
                  <Label className="mb-2 block">Aadhaar Card Photo *</Label>
                  <DocumentPicker label="Upload Aadhaar card" onFile={setAadhaarFile} testId="reg-aadhaar-doc" />
                </div>

                <div>
                  <Label>Gender *</Label>
                  <div className="grid grid-cols-3 gap-2 mt-1" data-testid="reg-gender-group">
                    {['male', 'female', 'other'].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, gender: g }))}
                        data-testid={`reg-gender-${g}`}
                        className={`h-12 rounded-md border-2 text-sm font-medium capitalize transition-colors ${
                          formData.gender === g ? 'border-primary bg-muted' : 'border-border hover:border-border'
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Riders can choose to be matched with female drivers only</p>
                </div>

                <div>
                  <Label htmlFor="address">Address *</Label>
                  <Input id="address" name="address" placeholder="Enter your full address"
                    value={formData.address} onChange={handleInputChange} className="h-12" required
                    data-testid="reg-address-input" />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Driving Details</h3>

                <div>
                  <Label htmlFor="drivingLicense">Driving License Number *</Label>
                  <Input id="drivingLicense" name="drivingLicense" placeholder="e.g., DL1420210012345"
                    value={formData.drivingLicense} onChange={handleInputChange} className="h-12" required
                    data-testid="reg-license-input" />
                </div>

                <div>
                  <Label className="mb-2 block">Driving License Photo *</Label>
                  <DocumentPicker label="Upload driving license" onFile={setLicenseFile} testId="reg-license-doc" />
                </div>

                <div>
                  <Label htmlFor="experience">Driving Experience (in years) *</Label>
                  <Input id="experience" name="experience" type="number" placeholder="e.g., 5"
                    value={formData.experience} onChange={handleInputChange} min="1" max="50"
                    className="h-12" required data-testid="reg-experience-input" />
                </div>
              </div>

              <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                <div className="flex gap-3">
                  <Upload className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-primary mb-1">Document Verification</h4>
                    <p className="text-sm text-primary">
                      After submission, our team will verify your documents. You'll receive your unique Driver ID and password instantly, and login will work after admin approval.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-background border rounded-lg p-4">
                <p className="text-sm text-foreground">
                  By submitting this form, you agree to our terms of service and confirm that all
                  information provided is accurate. False information may result in rejection or account suspension.
                </p>
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full bg-primary hover:bg-primary-dark text-lg"
                disabled={submitting}
                data-testid="reg-submit-btn"
              >
                {submitting ? 'Submitting…' : 'Submit Application'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DriverRegistration;
